<template>
  <q-section v-bind="$attrs" class="ui-checkin1" :bg-i="resolvedUiMeta.images.checkinEntry" @click="open()">
    <q-dialog
      :name="resolvedUiMeta.config.checkinDialogName"
      :bg-i="resolvedUiMeta.images.checkinDialogBg"
      pc="pc"
      :closeable="true"
      :close-button-props="resolvedUiMeta.layout.wrapper.closeButton">
      <q-section v-bind="resolvedUiMeta.layout.wrapper.tip" class="ws-nowrap">
        {{ tipText }}
      </q-section>
      <q-section v-bind="resolvedUiMeta.layout.wrapper.checkinContent">
        <q-section
          v-for="(row, rowIndex) in dayRows"
          :key="`checkin-row-${rowIndex}`"
          v-bind="resolvedUiMeta.layout.wrapper.row"
          class="flex flex-row items-center justify-center gap-x-2"
          p="relative"
          :style="{ height: `${100 / dayRows.length}%` }">
          <q-section
            v-for="day in row"
            :key="`checkin-day-${day.index}-${day.checked ? 'checked' : 'unchecked'}`"
            p="relative"
            v-bind="day.layout"
            :bg-i="day.image">
            <q-section
              v-if="day.checked"
              :bg-i="resolvedUiMeta.images.checkinIconChecked"
              v-bind="checkedIconPosition(rowIndex)"></q-section>
          </q-section>
        </q-section>
      </q-section>
      <q-section
        :key="`checkin-btn-${isSignToday ? 'got' : 'get'}`"
        v-bind="resolvedUiMeta.layout.wrapper.button"
        :bg-i="resolvedUiMeta.images[isSignToday ? 'checkinGot' : 'checkinGet']"
        @click="handleSignin"></q-section>
    </q-dialog>
  </q-section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import mergeWith from 'lodash/mergeWith'
import { useCheckin, defaultConfig, type CheckinDayImage, type CheckinUiMeta } from './use-checkin'

const props = defineProps<{
  uiMeta?: CheckinUiMeta
}>()

const emit = defineEmits<{
  (event: 'signed'): void
}>()

const resolvedUiMeta = computed(() =>
  mergeWith({}, defaultConfig, props.uiMeta || {}, (_targetValue, sourceValue) => {
    return Array.isArray(sourceValue) ? sourceValue.slice() : undefined
  })
)

const { progress, isSignToday, signin, open } = useCheckin(resolvedUiMeta.value)

const configuredDays = computed(() => {
  const days = resolvedUiMeta.value.images.checkinDays || []
  const configuredCount = Number(resolvedUiMeta.value.config.dayCount || days.length)
  return days.slice(0, Math.max(0, configuredCount))
})

const replacePattern = (pattern: string, day: string) => pattern.replace('{day}', day)

const resolveDayImage = (day: CheckinDayImage, checked: boolean) => {
  if (typeof day === 'object') {
    return checked ? day.checked || day.unchecked || day.key || '' : day.unchecked || day.checked || day.key || ''
  }
  const pattern = checked ? resolvedUiMeta.value.config.checkedImagePattern : resolvedUiMeta.value.config.uncheckedImagePattern
  return replacePattern(pattern, day)
}

const dayItems = computed(() =>
  configuredDays.value.map((day, index) => ({
    index,
    checked: progress.value >= index + 1,
    image: resolveDayImage(day, progress.value >= index + 1),
    layout: typeof day === 'object' ? day.layout || {} : {},
  }))
)

const dayRows = computed(() => {
  const items = dayItems.value
  const configuredRowCounts = resolvedUiMeta.value.config.rowCounts || []
  const columns = Math.max(1, Number(resolvedUiMeta.value.config.columns || 3))
  const rowCounts =
    configuredRowCounts.reduce((total, count) => total + Number(count || 0), 0) === items.length
      ? configuredRowCounts
      : Array.from({ length: Math.ceil(items.length / columns) }, (_, index) =>
          Math.min(columns, items.length - index * columns)
        )

  let cursor = 0
  return rowCounts.map(count => {
    const row = items.slice(cursor, cursor + Number(count))
    cursor += Number(count)
    return row
  })
})

const checkedIconPosition = (rowIndex: number) => {
  const positions = resolvedUiMeta.value.layout.wrapper.checkedIconPositions || []
  return positions[rowIndex] || positions[positions.length - 1] || {}
}

const tipText = computed(() => String(resolvedUiMeta.value.config.tipText || '').replace('{progress}', String(progress.value)))

const handleSignin = async () => {
  const success = await signin()
  if (success) {
    emit('signed')
  }
}
</script>

<style scoped lang="scss"></style>
